// ─────────────────────────────────────────────────────────────
// RichTextarea — textarea with a minimal markdown toolbar
// Supports: **bold**, *italic*, - bullet lists, numbered lists
// Content is stored as markdown text (plain text compatible)
// ─────────────────────────────────────────────────────────────

import { useRef } from 'react';
import {
  IconBold, IconItalic, IconList, IconListNumbers,
} from '@tabler/icons-react';

interface Props {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minRows?: number;
}

// ── Toolbar action helpers ────────────────────────────────────

function wrapSelection(
  textarea: HTMLTextAreaElement,
  before: string,
  after: string,
  setValue: (v: string) => void,
) {
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const text = textarea.value;
  const selected = text.slice(start, end);

  // Toggle: if already wrapped, unwrap
  const alreadyWrapped =
    text.slice(start - before.length, start) === before &&
    text.slice(end, end + after.length) === after;

  let newText: string;
  let newStart: number;
  let newEnd: number;

  if (alreadyWrapped) {
    newText =
      text.slice(0, start - before.length) +
      selected +
      text.slice(end + after.length);
    newStart = start - before.length;
    newEnd = end - before.length;
  } else {
    const replacement = selected || (before === '**' ? 'bold' : before === '*' ? 'italic' : '');
    newText = text.slice(0, start) + before + replacement + after + text.slice(end);
    newStart = start + before.length;
    newEnd = selected
      ? end + before.length
      : start + before.length + replacement.length;
  }

  setValue(newText);
  // Restore selection after state update
  requestAnimationFrame(() => {
    textarea.focus();
    textarea.setSelectionRange(newStart, newEnd);
  });
}

function insertBullet(
  textarea: HTMLTextAreaElement,
  prefix: string,
  setValue: (v: string) => void,
) {
  const start = textarea.selectionStart;
  const text = textarea.value;

  // Find the start of the current line
  const lineStart = text.lastIndexOf('\n', start - 1) + 1;
  const lineEnd = text.indexOf('\n', start);
  const line = text.slice(lineStart, lineEnd === -1 ? undefined : lineEnd);

  let newText: string;
  let cursorPos: number;

  if (line.startsWith(prefix)) {
    // Remove bullet from this line
    newText = text.slice(0, lineStart) + line.slice(prefix.length) + text.slice(lineEnd === -1 ? text.length : lineEnd);
    cursorPos = start - prefix.length;
  } else {
    // Add bullet to this line
    newText = text.slice(0, lineStart) + prefix + line + text.slice(lineEnd === -1 ? text.length : lineEnd);
    cursorPos = start + prefix.length;
  }

  setValue(newText);
  requestAnimationFrame(() => {
    textarea.focus();
    textarea.setSelectionRange(cursorPos, cursorPos);
  });
}

// ── Handle Enter to continue list items ──────────────────────

function handleKeyDown(
  e: React.KeyboardEvent<HTMLTextAreaElement>,
  setValue: (v: string) => void,
) {
  if (e.key !== 'Enter') return;
  const textarea = e.currentTarget;
  const start = textarea.selectionStart;
  const text = textarea.value;
  const lineStart = text.lastIndexOf('\n', start - 1) + 1;
  const line = text.slice(lineStart, start);

  // Continue `- ` bullet lists
  const bulletMatch = line.match(/^(\s*-\s)/);
  // Continue `1. ` numbered lists
  const numberedMatch = line.match(/^(\s*)(\d+)\.\s/);

  if (bulletMatch) {
    // If the current bullet line is empty (just `- `), end the list
    if (line.trim() === '-') {
      e.preventDefault();
      const newText = text.slice(0, lineStart) + '\n' + text.slice(start);
      setValue(newText);
      requestAnimationFrame(() => {
        textarea.focus();
        textarea.setSelectionRange(lineStart + 1, lineStart + 1);
      });
      return;
    }
    e.preventDefault();
    const insert = '\n' + bulletMatch[1];
    const newText = text.slice(0, start) + insert + text.slice(start);
    const pos = start + insert.length;
    setValue(newText);
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(pos, pos);
    });
  } else if (numberedMatch) {
    if (line.trim() === numberedMatch[2] + '.') {
      e.preventDefault();
      const newText = text.slice(0, lineStart) + '\n' + text.slice(start);
      setValue(newText);
      requestAnimationFrame(() => {
        textarea.focus();
        textarea.setSelectionRange(lineStart + 1, lineStart + 1);
      });
      return;
    }
    e.preventDefault();
    const nextNum = parseInt(numberedMatch[2]) + 1;
    const insert = '\n' + numberedMatch[1] + nextNum + '. ';
    const newText = text.slice(0, start) + insert + text.slice(start);
    const pos = start + insert.length;
    setValue(newText);
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(pos, pos);
    });
  }
}

// ── Component ─────────────────────────────────────────────────

export default function RichTextarea({ value, onChange, placeholder, minRows = 4 }: Props) {
  const ref = useRef<HTMLTextAreaElement>(null);

  const toolbarBtn = (onClick: () => void, icon: React.ReactNode, title: string) => (
    <button
      type="button"
      title={title}
      onMouseDown={e => { e.preventDefault(); onClick(); }}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 26, height: 26, border: '1px solid #e2e8f0', borderRadius: 4,
        background: 'white', cursor: 'pointer', color: '#475569',
        flexShrink: 0,
      }}
      onMouseEnter={e => (e.currentTarget.style.background = '#f1f5f9')}
      onMouseLeave={e => (e.currentTarget.style.background = 'white')}
    >
      {icon}
    </button>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 4, alignItems: 'center', padding: '3px 0' }}>
        {toolbarBtn(
          () => ref.current && wrapSelection(ref.current, '**', '**', onChange),
          <IconBold size={13} />, 'Bold (Ctrl+B)',
        )}
        {toolbarBtn(
          () => ref.current && wrapSelection(ref.current, '*', '*', onChange),
          <IconItalic size={13} />, 'Italic (Ctrl+I)',
        )}
        <div style={{ width: 1, height: 16, background: '#e2e8f0', margin: '0 2px' }} />
        {toolbarBtn(
          () => ref.current && insertBullet(ref.current, '- ', onChange),
          <IconList size={13} />, 'Bullet list',
        )}
        {toolbarBtn(
          () => ref.current && insertBullet(ref.current, '1. ', onChange),
          <IconListNumbers size={13} />, 'Numbered list',
        )}
        <span style={{ fontSize: 12, color: '#939598', marginLeft: 4 }}>
          Select text then click, or click to insert
        </span>
      </div>

      {/* Textarea */}
      <textarea
        ref={ref}
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => handleKeyDown(e, onChange)}
        placeholder={placeholder}
        rows={minRows}
        style={{
          width: '100%',
          padding: '8px 8px',
          border: '1px solid #ced4da',
          borderRadius: 4,
          fontSize: 14,
          fontFamily: 'inherit',
          lineHeight: 1.6,
          resize: 'vertical',
          outline: 'none',
          boxSizing: 'border-box',
          color: '#1B1C1C',
          background: 'white',
        }}
        onFocus={e => (e.target.style.borderColor = '#4B3FFF')}
        onBlur={e => (e.target.style.borderColor = '#ced4da')}
      />
    </div>
  );
}
